import Box from "@mui/material/Box";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import type React from "react";

import { type DriverRow, isSamsaraUsernameTag, type TagDefinition } from "@/types/driver";
import ColumnHeader from "@/ui/ColumnHeader";
import type { DataTableFeatures } from "@/ui/dataTableFeatures";
import { TagChipCell } from "@/ui/TagChipCell";
import { columnWidthFloors, minWidthForColumnHeader, minWidthForColumnIcons } from "./columnMinWidth";
import { DriverFeedNotificationIcon, driverNameColumn } from "./driverNameColumn";
import { dutyStatusColumn } from "./dutyStatusColumn";
import { inboxColumn } from "./inboxColumn";
import { recentMessageColumn } from "./recentMessageColumn";
import { statusColumn } from "./statusColumn";
import { volumeColumn } from "./volumeColumn";

export type ColumnFilterOptions = {
  getColumnExtra: (key: string) => React.ReactNode;
  isFilterActive: (key: string) => boolean;
  getActiveFilterValues: (key: string) => string[];
  showUnreadOnly: boolean;
  toggleUnreadOnly: () => void;
  /** Unactivated (never-logged-in) membership ids — drives the availability cell label. */
  unactivatedMembershipIds?: ReadonlySet<string>;
};

/** Tag columns share the standard header budget but never start narrower than 200px on first load. */
function initialMinWidthForTagColumnLabel(label: string) {
  return Math.max(200, minWidthForColumnHeader(label, { draggable: true, hasFilter: true }));
}

/**
 * Locate the workspace tag promoted to the leading frozen column, if it exists.
 *
 * Matches the Samsara username tag by known `tag.label` values until bootstrap exposes
 * `WorkspaceIntegrationTagConfig.source`.
 */
export function findDriverUsernameTag(tags: readonly TagDefinition[]): TagDefinition | undefined {
  return tags.find((t) => isSamsaraUsernameTag(t));
}

function buildTagColumn(
  options: ColumnFilterOptions,
  t: TagDefinition,
  overrides?: {
    frozen?: boolean;
    /** Single-value tags only: show the feed notification icon in this cell (e.g. leading username column). */
    showNotificationBell?: boolean;
    headerTitle?: string;
    size?: number;
    enableHiding?: boolean;
  },
): ColumnDef<DataTableFeatures, DriverRow> {
  const key = `tag:${t.id}`;
  const isFrozen = overrides?.frozen === true;
  const headerTitle = overrides?.headerTitle ?? t.label;
  const header = () => (
    <ColumnHeader title={headerTitle} extra={options.getColumnExtra(key)} isActive={options.isFilterActive(key)} />
  );
  const base = {
    id: key,
    header,
    meta: {
      flex: isFrozen ? undefined : 1.5,
      draggable: !isFrozen,
      frozen: isFrozen,
      initialMinWidth: initialMinWidthForTagColumnLabel(headerTitle),
    },
    minSize: minWidthForColumnIcons({ draggable: !isFrozen, hasFilter: true }),
    ...(overrides?.size != null && { size: overrides.size }),
    enableSorting: false,
    enableGlobalFilter: false,
    ...(overrides?.enableHiding === false && { enableHiding: false }),
  };

  if (t.multiValue) {
    return {
      ...base,
      accessorFn: (row: DriverRow) => row.tags[t.id] ?? [],
      cell: (info: CellContext<DataTableFeatures, DriverRow, unknown>) => (
        <TagChipCell values={(info.getValue() as string[]) ?? []} activeFilter={options.getActiveFilterValues(key)} />
      ),
    };
  }

  const showBell = overrides?.showNotificationBell === true;

  return {
    ...base,
    accessorFn: (row: DriverRow) => row.tags[t.id]?.[0] ?? "",
    ...(showBell && {
      cell: ({ row, getValue }) => (
        <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
            {String(getValue() ?? "")}
          </Box>
          <DriverFeedNotificationIcon feedPrefId={row.original.feedPrefId} />
        </Box>
      ),
    }),
  };
}

export function buildDriverColumns(
  options: ColumnFilterOptions,
  tags: TagDefinition[],
): ColumnDef<DataTableFeatures, DriverRow>[] {
  const driverUsernameTag = findDriverUsernameTag(tags);
  const otherTags = driverUsernameTag ? tags.filter((t) => t.id !== driverUsernameTag.id) : tags;

  const otherTagColumns = otherTags.map((t) => buildTagColumn(options, t));

  const usernameBellInTagColumn = Boolean(driverUsernameTag && !driverUsernameTag.multiValue);

  const groupFloors = columnWidthFloors("Group", { draggable: true, hasFilter: true });
  const locationFloors = columnWidthFloors("Location", { draggable: true, hasFilter: true });
  const stateProvinceFloors = columnWidthFloors("State/Province", { draggable: true, hasFilter: false });
  const countryFloors = columnWidthFloors("Country", { draggable: true, hasFilter: false });

  const leadingUsernameColumn = driverUsernameTag
    ? buildTagColumn(options, driverUsernameTag, {
        frozen: true,
        showNotificationBell: usernameBellInTagColumn,
        size: 240,
        enableHiding: false,
      })
    : null;

  return [
    inboxColumn(options),
    ...(leadingUsernameColumn ? [leadingUsernameColumn] : []),
    driverNameColumn(options, {
      frozen: !leadingUsernameColumn,
      includeSort: true,
      includeNotificationBell: !usernameBellInTagColumn,
    }),
    recentMessageColumn(),
    {
      accessorKey: "group",
      header: () => (
        <ColumnHeader
          title="Group"
          extra={options.getColumnExtra("group")}
          isActive={options.isFilterActive("group")}
        />
      ),
      meta: { flex: 1.5, draggable: true, initialMinWidth: groupFloors.initialMinWidth },
      minSize: groupFloors.minSize,
      enableSorting: false,
      enableGlobalFilter: false,
    },
    statusColumn(options),
    dutyStatusColumn(options),
    {
      accessorKey: "location",
      header: () => (
        <ColumnHeader
          title="Location"
          extra={options.getColumnExtra("location")}
          isActive={options.isFilterActive("location")}
        />
      ),
      meta: { flex: 2, draggable: true, initialMinWidth: locationFloors.initialMinWidth },
      minSize: locationFloors.minSize,
      enableSorting: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "stateProvince",
      header: () => <ColumnHeader title="State/Province" />,
      meta: { flex: 1.5, draggable: true, initialMinWidth: stateProvinceFloors.initialMinWidth },
      minSize: stateProvinceFloors.minSize,
      enableSorting: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "country",
      header: () => <ColumnHeader title="Country" />,
      meta: { flex: 1, draggable: true, initialMinWidth: countryFloors.initialMinWidth },
      minSize: countryFloors.minSize,
      enableSorting: false,
      enableGlobalFilter: false,
    },
    volumeColumn(options),
    ...otherTagColumns,
  ];
}
